import { getPromoUIMap } from '../../helpers/getPromoUIMap';
import {
  getCompletePromoConfig,
  getCompletePricingPromoConfig
} from '../../helpers/promotions';
import { FeatureToggleStatus } from '../../modules/feature-toggles/feature-toggles';
import { CreditApplicationStatus } from '../../services/credit';
import { PlanPrices } from '../../utils/prices';
import { getPlanNameText } from '../Widgets/CreditApplication/helpers';

interface Child {
  firstName: string;
}

export const buildCompleteHeading = (
  children: Child[],
  promoId: string,
  answers,
  isCreditFlow: boolean,
  improvedDownloadScreen = false
) => {
  if (isCreditFlow) {
    return answers.creditApplication.creditAccepted
      ? 'Your credit card is on the way'
      : 'Download the Greenlight app';
  }

  if (improvedDownloadScreen) {
    return 'Download the Greenlight app';
  }

  return buildChildrenHeading(children, promoId);
};

export const buildChildrenHeading = (children: Child[], promoId: string) => {
  if (!children || !children.length) {
    return 'We are preparing your card!';
  }

  const childrenNames = children.map(child => child.firstName);

  const promoConfig = getCompletePromoConfig(promoId, childrenNames);

  if (promoConfig && promoConfig.header) {
    return promoConfig.header;
  }

  if (childrenNames.length === 1) {
    return `We are preparing ${childrenNames[0]}'s card!`;
  }

  if (childrenNames.length === 2) {
    return `We are preparing ${childrenNames[0]} and ${childrenNames[1]}'s cards!`;
  }

  const last = childrenNames.pop();
  return `We are preparing ${childrenNames.join(', ')}, and ${last}'s cards!`;
};

interface AppConfirmationMessageProps {
  answers: {
    setRole: 'parent' | 'approver' | 'child';
    setInitialFundingAmount: number;
    setFundingAccount: {
      source: string;
    };
  };
  needsVerification?: boolean;
}

export const buildConfirmationMessage = (
  answers: any,
  registeredUser: any,
  needsVerification: boolean,
  isCreditFlow: boolean
) => {
  const initialFundingAmount = answers.setInitialFundingAmount;
  const isParent = answers.setRole === 'parent';
  const isApprover = answers.setRole === 'approver';
  if (isCreditFlow) {
    if (answers.creditApplication.creditAccepted) {
      return `Download the app to manage credit card payments, view transaction history, and redeem cash back.\n\nWith your ${getPlanNameText(
        answers.setPlan?.pricingPlanName
      )} plan, you can also manage your family finances — like sending money to your kids, ordering debit cards, setting up chores, and investing as a family.`;
    }

    return answers.creditApplication.creditDecision ===
      CreditApplicationStatus.DELAYED
      ? `With your Greenlight ${getPlanNameText(
          answers.setPlan?.pricingPlanName
        )} plan, you can manage your family finances — like sending money to your kids, ordering debit cards, setting up chores, and investing as a family.\n\nIf you are approved for the Family Cash Card, you can also manage card payments, view transaction history, and redeem cash back in the app.`
      : 'Get the app to order debit cards for your kids, send them money, set up chores, and more.';
  }

  if (isParent) {
    const customConfirmationMessage = registeredUser.confirmationMessage;
    if (customConfirmationMessage) return customConfirmationMessage;

    if (initialFundingAmount && initialFundingAmount >= 1) {
      if (needsVerification) {
        const isDebit =
          answers.setFundingAccount &&
          answers.setFundingAccount.source === 'debit';
        return `$${initialFundingAmount} will be added to your account once the ${
          isDebit ? 'card' : 'bank'
        } is verified.\n\nDownload the app now to make a Custom Card and finish setting up your account.`;
      }

      return `$${initialFundingAmount} has been added to your account.\n\nDownload the app now to make a Custom Card and finish setting up your account.`;
    }
    return `Download the app now to make a Custom Card and finish setting up your account.`;
  }

  if (isApprover) return `You're registered as part of this account!`;
};

export const getPromoConfig = (promoId: string, uiMap: any) => {
  return uiMap[promoId] || uiMap.default;
};

export const getPricingMessageForPlan = (
  planName: string,
  promoConfig: any
) => {
  const { pricingMessage } = promoConfig[planName] || promoConfig.standard;
  return pricingMessage;
};

export const buildPricingMessage = ({
  hasMultipleChildren,
  plans,
  promoId,
  selectedPlanName,
  answers,
  riskFreePaintedDoorEnabled
}: {
  hasMultipleChildren: boolean;
  plans: PlanPrices;
  promoId: string;
  selectedPlanName: string;
  answers: any;
  riskFreePaintedDoorEnabled?: boolean;
}) => {
  const promoUIMap = getPromoUIMap(plans);
  const promoConfig = getPromoConfig(promoId, promoUIMap);
  const pricingMessage = getPricingMessageForPlan(
    selectedPlanName,
    promoConfig
  );

  const promoPricingMessage = getCompletePricingPromoConfig(
    promoId,
    selectedPlanName,
    plans.standard,
    hasMultipleChildren
  );

  if (answers.creditApplication) {
    return answers.creditApplication.creditDecision ===
      CreditApplicationStatus.ACCEPTED
      ? `${
        riskFreePaintedDoorEnabled ? 
          `Your` : 
          `Your ${getPlanNameText(answers.setPlan?.pricingPlanName)} free trial starts today. After your 1-month free trial, your`
        } plan is $${
          plans.standard
        }/mo. Please allow 7-10 business days for delivery of your credit card.`
      : `${
        riskFreePaintedDoorEnabled ? 
          `Your` : 
          `Your free ${getPlanNameText(answers.setPlan?.pricingPlanName)} trial starts today. After your 1-month free trial, your` 
        } plan is $${
          plans.standard
        }/mo.`;
  }

  if (promoPricingMessage) {
    return promoPricingMessage;
  }

  return `${ 
    riskFreePaintedDoorEnabled ? 
      `Your plan is $${
        plans[selectedPlanName] || plans.standard
      }/mo` : 
      `Your free trial starts today. ${pricingMessage} $${
        plans[selectedPlanName] || plans.standard
      }/mo` 
    }. Please allow 7 - 10 business days for delivery of your card${
    hasMultipleChildren ? 's' : ''
  }.`;
};
